/**
 * Device and connectivity hooks for Offline Field Mode (Phase 16A, design §2.1 / §3.2).
 *
 * - useDeviceClass: MOBILE (phone/tablet, any width) or DESKTOP, from the OS.
 * - useFieldMode: touch layout for clinicians on phones and tablets, including ≥768 px tablets
 *   that the width-based useMobileDetection treats as desktop.
 * - useOnlineStatus: navigator.onLine with online/offline events.
 * - useOfflineStatus: the offline session state (none / locked / unlocked).
 */
import { useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { detectDeviceClass, isStandalone, DeviceClass } from '../services/offline/platform/deviceClass';
import { OfflineSession, OfflineStatus } from '../services/offline/offlineSession';

export function useDeviceClass(): DeviceClass {
  return useMemo(() => detectDeviceClass(), []);
}

const coarsePointer = () => typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;

export function useFieldMode(isClinician: boolean): boolean {
  const deviceClass = useDeviceClass();
  const [coarse, setCoarse] = useState(coarsePointer);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const query = window.matchMedia('(pointer: coarse)');
    const onChange = () => setCoarse(query.matches);
    query.addEventListener?.('change', onChange);
    return () => query.removeEventListener?.('change', onChange);
  }, []);
  return isClinician && deviceClass === 'MOBILE' && (coarse || isStandalone());
}

const subscribeOnline = (callback: () => void) => {
  window.addEventListener('online', callback);
  window.addEventListener('offline', callback);
  return () => {
    window.removeEventListener('online', callback);
    window.removeEventListener('offline', callback);
  };
};

export function useOnlineStatus(): boolean {
  return useSyncExternalStore(subscribeOnline, () => navigator.onLine, () => true);
}

export function useOfflineStatus(): OfflineStatus {
  return useSyncExternalStore(
    (cb) => OfflineSession.subscribe(cb),
    () => OfflineSession.status,
    () => 'none' as OfflineStatus,
  );
}
